import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import clsx from "clsx";

import { makeRequest } from "../../utils/request";
import { Button, EmptyState, Spinner, Text, View } from "../../components/base";
import { Icon } from "../../components/base/Icon";
import { FineTuningPipelineStepper } from "../../components/FineTuningPipelineStepper";
import { FineTuningJobStatusPill } from "../../components/FineTuningJobStatusPill";
import { FineTuningMutationNotice, useMutationNotice } from "../../components/FineTuningMutationNotice";
import { TrainingMetricsChart, type TrainingMetrics } from "../../components/charts";

type FineTuningJob = {
  id: string;
  status: string;
  phase: string | null;
  org_id: string;
  model: string;
  // two orthogonal axes (was the single `method`): training_type = weight (lora/qlora/full),
  // training_method = objective (sft/dpo).
  training_type: string;
  training_method: string;
  training_file: string;
  validation_file: string | null;
  suffix: string | null;
  hyperparameters: Record<string, unknown>; // what was REQUESTED
  // what actually RAN — micro_batch_size/grad-accum derived + capped to the GPU, optimizer/LR
  // possibly method-defaulted (full FT). Parsed server-side from the rendered recipe; null if unrendered.
  effective_config?: Record<string, unknown> | null;
  output_name: string | null;
  output_adapter_uri: string | null;
  error: string | null;
  created_at: string;
  finished_at: string | null;
  metrics?: TrainingMetrics;
  // Live training progress, streamed from the ckpt-sync sidecar's trainer_state.json while the
  // job is RUNNING. Absent for jobs that never ran / old jobs — always guard on presence.
  progress?: TrainingProgress | null;
};

type TrainingProgress = {
  current_step: number;
  total_steps: number | null;
  epoch: number;
  total_epochs: number;
  loss: number | null;
  eval_loss: number | null;
  pct: number;
  eta_seconds: number | null;
  started_at: string;
  updated_at: string;
};

type JobEvent = {
  id: string;
  type: string;
  level: string;
  message: string;
  created_at: string;
};

const TERMINAL = new Set(["succeeded", "failed", "cancelled"]);
const POLL_MS = 6000;
// Org-scoping is bypassed here: this is a platform-admin view across every org (the page is
// admin-only). Each row shows which org the job belongs to.
const ALL_ORGS = "all_orgs=true";

const COLUMNS = "1.4fr 1.4fr 1.3fr 0.8fr 0.9fr 1fr 1fr";

// Timestamps from the control plane may be naive UTC (no offset); tag them UTC so the browser
// doesn't parse them as local time (which would skew every relative time).
const asUtc = (iso: string) => (/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);

const relativeTime = (iso: string | null): string => {
  if (!iso) return "—";
  const ms = Date.now() - new Date(asUtc(iso)).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

const duration = (start: string, end: string | null): string => {
  if (!end) return "—";
  const s = Math.max(0, Math.floor((new Date(asUtc(end)).getTime() - new Date(asUtc(start)).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
};

const shortId = (id: string) => (id.length > 16 ? `${id.slice(0, 14)}…` : id);

// null eta -> "estimating…"; otherwise a compact "2d 3h" / "1h 16m" / "6m" / "<1m".
const humanizeEta = (seconds: number | null | undefined): string => {
  if (seconds == null || !Number.isFinite(seconds)) return "estimating…";
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return "<1m";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
};

const clamp01 = (n: number) => (Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0);

export const AdminFineTuningJobsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("id");

  const [jobs, setJobs] = useState<FineTuningJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadJobs = useCallback(async (showSpinner = false) => {
    if (showSpinner) setIsLoading(true);
    try {
      const { data } = await makeRequest<{ data: FineTuningJob[] }>(`/v1/fine_tuning/jobs?${ALL_ORGS}&limit=200`);
      setJobs(data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load fine-tuning jobs");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadJobs(true);
  }, [loadJobs]);

  // Poll while any job is non-terminal so statuses update live.
  const hasActive = jobs.some((j) => !TERMINAL.has(j.status));
  useEffect(() => {
    if (!hasActive) return;
    const t = setInterval(() => loadJobs(false), POLL_MS);
    return () => clearInterval(t);
  }, [hasActive, loadJobs]);

  const selected = jobs.find((j) => j.id === selectedId) || null;

  return (
    <View className="h-full w-full bg-gray-50 dark:bg-slate-900">
      <div className={clsx("h-full w-full relative transition-all duration-300", selectedId && "pr-[440px]")}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <Text className="text-xl font-semibold">Fine-Tuning Trainings</Text>
            <Text className="text-sm text-gray-500 dark:text-gray-400">
              {jobs.length} {jobs.length === 1 ? "training" : "trainings"} · all orgs
            </Text>
            {hasActive && <Spinner className="!h-4 !w-4 !text-brand" />}
          </div>
          <Button
            variant="ghost"
            type="button"
            onClick={() => loadJobs(true)}
          >
            <Icon
              name="refresh"
              size={18}
              className="text-brand"
            />
          </Button>
        </div>

        {/* Column headers */}
        <FineTuningPipelineStepper />

        <div
          className="grid border-b-2 border-gray-200 dark:border-gray-700 px-6"
          style={{ gridTemplateColumns: COLUMNS }}
        >
          {["Org", "Training", "Model", "Type", "Status", "Created", "Duration"].map((h) => (
            <span
              key={h}
              className="py-3 pr-2 text-sm font-semibold text-gray-700 dark:text-gray-200"
            >
              {h}
            </span>
          ))}
        </div>

        {/* Rows */}
        <div
          className="overflow-y-auto"
          style={{ height: "calc(100% - 110px)" }}
        >
          {isLoading ? (
            <View className="flex items-center justify-center py-20">
              <Spinner className="!text-brand" />
            </View>
          ) : error ? (
            <View className="flex items-center justify-center py-20">
              <Text className="text-error">{error}</Text>
            </View>
          ) : jobs.length === 0 ? (
            <EmptyState
              icon="bot"
              title="No fine-tuning trainings yet"
              description="Trainings created via `vr ft create` will appear here, across all organizations."
            />
          ) : (
            jobs.map((job) => (
              <div
                key={job.id}
                onClick={() => setSearchParams({ id: job.id })}
                className={clsx(
                  "grid items-center px-6 border-b border-gray-100 dark:border-gray-800 cursor-pointer",
                  "hover:bg-gray-100 dark:hover:bg-gray-800",
                  selectedId === job.id && "bg-gray-100 dark:bg-gray-800",
                )}
                style={{ gridTemplateColumns: COLUMNS }}
              >
                <span
                  className="py-3 pr-2 truncate text-sm text-gray-500 dark:text-gray-400"
                  title={job.org_id}
                >
                  {job.org_id}
                </span>
                <span
                  className="py-3 pr-2 truncate text-sm font-mono"
                  title={job.id}
                >
                  {shortId(job.id)}
                </span>
                <span
                  className="py-3 pr-2 truncate text-sm"
                  title={job.model}
                >
                  {job.model}
                </span>
                <span className="py-3 pr-2 truncate text-sm uppercase text-gray-500 dark:text-gray-400">
                  {job.training_type}
                  {job.training_method === "dpo" ? " · DPO" : ""}
                </span>
                <span className="py-3 pr-2">
                  <FineTuningJobStatusPill
                    status={job.status}
                    phase={job.phase}
                  />
                </span>
                <span className="py-3 pr-2 truncate text-sm text-gray-500 dark:text-gray-400">{relativeTime(job.created_at)}</span>
                <span className="py-3 pr-2 truncate text-sm text-gray-500 dark:text-gray-400">{duration(job.created_at, job.finished_at)}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {selected && (
        <JobDetailPanel
          job={selected}
          onClose={() => setSearchParams({})}
          onChanged={() => loadJobs(false)}
        />
      )}
    </View>
  );
};

type JobDetailPanelProps = {
  job: FineTuningJob;
  onClose: () => void;
  onChanged: () => void;
};

const JobDetailPanel = ({ job, onClose, onChanged }: JobDetailPanelProps) => {
  const [events, setEvents] = useState<JobEvent[]>([]);
  const [isCancelling, setIsCancelling] = useState(false);
  const { notice, showError, showSuccess, clearNotice } = useMutationNotice();
  const jobIdRef = useRef(job.id);
  jobIdRef.current = job.id;

  // a notice belongs to the job it was shown for — drop it when switching rows
  useEffect(() => clearNotice(), [job.id, clearNotice]);

  const isTerminal = TERMINAL.has(job.status);

  const loadEvents = useCallback(async () => {
    try {
      const { data } = await makeRequest<{ data: JobEvent[] }>(`/v1/fine_tuning/jobs/${job.id}/events?all_orgs=true`);
      // guard against a stale response after switching jobs
      if (jobIdRef.current === job.id) setEvents(data);
    } catch {
      /* events are best-effort */
    }
  }, [job.id]);

  useEffect(() => {
    setEvents([]);
    loadEvents();
    if (isTerminal) return;
    const t = setInterval(loadEvents, POLL_MS);
    return () => clearInterval(t);
  }, [loadEvents, isTerminal]);

  const onCancel = async () => {
    setIsCancelling(true);
    clearNotice();
    try {
      await makeRequest(`/v1/fine_tuning/jobs/${job.id}/cancel?all_orgs=true`, { method: "POST" });
      showSuccess("Cancellation requested.");
      onChanged();
      loadEvents();
    } catch (e) {
      showError(e, "Failed to cancel the job");
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <div className="fixed right-0 top-0 h-screen w-[440px] bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 flex flex-col z-20">
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Text className="text-base font-semibold">Training Details</Text>
          <FineTuningJobStatusPill
            status={job.status}
            phase={job.phase}
          />
        </div>
        <Button
          variant="ghost"
          type="button"
          onClick={onClose}
        >
          <Icon
            name="close"
            size={18}
            className="text-gray-500"
          />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        <TrainingProgressSection
          status={job.status}
          progress={job.progress}
        />
        <Section
          label="Job ID"
          value={job.id}
          mono
        />
        <Section
          label="Organization"
          value={job.org_id}
          mono
        />
        <Section
          label="Model"
          value={job.model}
        />
        <Field
          label="Training type"
          value={job.training_type.toUpperCase()}
        />
        <Field
          label="Training method"
          value={job.training_method.toUpperCase()}
        />
        {job.suffix && (
          <Field
            label="Suffix"
            value={job.suffix}
          />
        )}
        <Field
          label="Created"
          value={new Date(asUtc(job.created_at)).toLocaleString()}
        />
        {job.finished_at && (
          <Field
            label="Finished"
            value={new Date(asUtc(job.finished_at)).toLocaleString()}
          />
        )}
        <Field
          label="Training file"
          value={job.training_file}
          mono
        />
        {job.validation_file && (
          <Field
            label="Validation file"
            value={job.validation_file}
            mono
          />
        )}
        {job.output_adapter_uri && (
          <Section
            label="Adapter"
            value={job.output_adapter_uri}
            mono
          />
        )}
        {job.error && (
          <div>
            <Text className="text-xs uppercase text-gray-400 mb-1">Error</Text>
            <Text className="text-sm text-error whitespace-pre-wrap break-words">{job.error}</Text>
          </div>
        )}

        <div>
          <Text className="text-xs uppercase text-gray-400 mb-1">Hyperparameters (requested)</Text>
          <pre className="text-xs bg-gray-50 dark:bg-gray-900 rounded-md p-3 overflow-x-auto">{JSON.stringify(job.hyperparameters, null, 2)}</pre>
        </div>

        {job.effective_config && (
          <div>
            <Text className="text-xs uppercase text-gray-400 mb-1">Effective config (as run)</Text>
            <Text className="text-[11px] text-gray-400 mb-1">
              micro-batch &amp; grad-accum are derived from batch_size and capped to fit the GPU; optimizer/LR may be method-defaulted (full FT). This is what
              the rendered recipe actually trained with.
            </Text>
            <pre className="text-xs bg-gray-50 dark:bg-gray-900 rounded-md p-3 overflow-x-auto">{JSON.stringify(job.effective_config, null, 2)}</pre>
          </div>
        )}

        {job.metrics?.history?.length ? (
          <div>
            <Text className="text-xs uppercase text-gray-400 mb-2">Training metrics</Text>
            <TrainingMetricsChart metrics={job.metrics} />
          </div>
        ) : null}

        <div>
          <Text className="text-xs uppercase text-gray-400 mb-2">Events</Text>
          {events.length === 0 ? (
            <Text className="text-sm text-gray-400">No events yet.</Text>
          ) : (
            <div className="space-y-2">
              {events.map((e) => (
                <div
                  key={e.id}
                  className="flex gap-2 text-sm"
                >
                  <span
                    className={clsx(
                      "mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0",
                      e.level === "error" ? "bg-error" : e.level === "warning" ? "bg-yellow-500" : "bg-info",
                    )}
                  />
                  <div className="min-w-0">
                    <Text className="block text-gray-800 dark:text-gray-200 break-words">{e.message}</Text>
                    <Text className="block text-xs text-gray-400 mt-0.5">{relativeTime(e.created_at)}</Text>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* outside the terminal guard so a success notice survives the status flipping terminal */}
      <FineTuningMutationNotice
        notice={notice}
        className="px-4 py-3"
      />

      {!isTerminal && (
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            className="w-full"
            variant="ghost"
            type="button"
            onClick={onCancel}
            isLoading={isCancelling}
          >
            <Text className="text-error">Cancel job</Text>
          </Button>
        </div>
      )}
    </div>
  );
};

// Live progress for a running job. Renders NOTHING unless the job is actively training and the
// pipeline has streamed a `progress` object (old / not-yet-running jobs -> null -> no box).
const TrainingProgressSection = ({ status, progress }: { status: string; progress?: TrainingProgress | null }) => {
  if (status !== "running" || !progress) return null;

  const pct = clamp01(progress.pct);
  // null-safe epoch; drop the "/total" suffix if total_epochs is missing (never happens for real jobs — n_epochs defaults to 5 — but keeps the render robust).
  const epochStr = `${(progress.epoch ?? 0).toFixed(2)}${progress.total_epochs ? `/${progress.total_epochs}` : ""}`;
  const stepLine =
    progress.total_steps != null
      ? `step ${progress.current_step}/${progress.total_steps} · epoch ${epochStr}`
      : `step ${progress.current_step} · epoch ${epochStr}`;

  const lossParts: string[] = [];
  if (progress.loss != null) lossParts.push(`loss ${progress.loss.toFixed(4)}`);
  if (progress.eval_loss != null) lossParts.push(`eval ${progress.eval_loss.toFixed(4)}`);

  return (
    <div>
      <Text className="text-xs uppercase text-gray-400 mb-2">Training progress</Text>
      <div className="h-2 w-full rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
        <div
          className="h-full rounded-full bg-info transition-all duration-500"
          style={{ width: `${(pct * 100).toFixed(1)}%` }}
        />
      </div>
      <Text className="block text-sm text-gray-700 dark:text-gray-200 mt-2">{stepLine}</Text>
      {lossParts.length > 0 && <Text className="block text-sm text-gray-500 dark:text-gray-400 mt-0.5">{lossParts.join(" · ")}</Text>}
      <Text className="block text-xs text-gray-400 mt-0.5">~{humanizeEta(progress.eta_seconds)} remaining</Text>
    </div>
  );
};

const Field = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div className="flex items-baseline justify-between gap-3">
    <Text className="text-xs uppercase text-gray-400 flex-shrink-0">{label}</Text>
    <Text className={clsx("text-sm text-right break-words", mono && "font-mono text-xs")}>{value}</Text>
  </div>
);

const Section = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div>
    {/* the design-system Text is inline; `block` keeps the value on its own line under the label */}
    <Text className="block text-xs uppercase text-gray-400 mb-1">{label}</Text>
    <Text className={clsx("block text-sm break-all", mono && "font-mono text-xs")}>{value}</Text>
  </div>
);
